import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { MdDelete, MdEdit } from 'react-icons/md';
import { FaArrowLeft, FaSyncAlt, FaUserPlus, FaCopy, FaList, FaEllipsisV } from 'react-icons/fa';
import { LuMerge } from 'react-icons/lu';
import { IconButton, ActionDropdown, confirmAlert } from 'components/common';
import RoleForm from '../RoleForm';
import RoleDetailClone from './RoleDetailClone';
import { deleteRole, getAllRoles, getRole } from 'redux/authorization/roleSlice';
import CombineRolesDrawer from '../CombineRolesDrawer';

const RoleDetailHeader = ({ role, setTrigger }) => {
  const [showRoleForm,  setShowRoleForm]  = useState(false);
  const [showCloneForm, setShowCloneForm] = useState(false);
  const [title,         setTitle]         = useState('');
  const [roleData,      setRoleData]      = useState(role);
  const [showCombine,   setShowCombine]   = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const roleUrl  = '/authorization/roles';

  // Danh sách role nguồn chỉ cần khi mở drawer — không nạp sẵn cho mọi lần xem.
  const { allData } = useSelector(state => state.authorization_role);
  useEffect(() => {
    if (showCombine) dispatch(getAllRoles({ per_page: 1000 }));
  }, [showCombine, dispatch]);

  const combinableRoles = useMemo(() => {
    const rows = allData?.rows || allData?.data || [];
    const list = rows.map(row => row.attributes || row).filter(Boolean);
    // Role đang xem luôn là đích, nên nó phải có mặt kể cả khi trang đầu không chứa.
    return role && !list.some(r => r.id === role.id) ? [role, ...list] : list;
  }, [allData, role]);

  const handleNewRole  = () => { setTitle('New Role');  setShowRoleForm(true);  setRoleData(null); };
  const handleEditRole = () => { setTitle('Edit Role'); setShowRoleForm(true);  setRoleData(role); };
  const handleRoleList = () => navigate(roleUrl);
  const handleRefresh  = () => setTrigger(v => v + 1);

  const handleDeleteRole = () => {
    confirmAlert({
      title: 'Delete Role',
      message: `Are you sure you want to delete role <strong class="tw-text-red-600">${role?.name || ''}</strong>? This action cannot be undone.`,
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          label: 'Delete',
          onClick: async () => {
            try {
              await dispatch(deleteRole(role)).unwrap();
              navigate(roleUrl);
            } catch (error) {
              toast.error(error?.message || 'Failed to delete role');
            }
          }
        },
        { label: 'Cancel', onClick: () => {} }
      ]
    });
  };

  return (
    <>
      <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-gray-200 tw-shadow-sm tw-mb-3">
        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3">
          <IconButton
            icon={<FaArrowLeft size={12} />}
            onClick={() => navigate(-1)}
            title="Go back"
          />
          <div className="tw-flex tw-items-center tw-gap-1">
            <IconButton
              icon={<FaSyncAlt size={12} />}
              onClick={handleRefresh}
              title="Refresh"
            />
            <IconButton
              icon={<FaUserPlus size={12} />}
              onClick={handleNewRole}
              title="New role"
            >
              <span className="tw-hidden xl:tw-inline">New</span>
            </IconButton>
            <IconButton
              icon={<FaCopy size={12} />}
              onClick={() => setShowCloneForm(true)}
              title="Clone role"
            >
              <span className="tw-hidden xl:tw-inline">Clone</span>
            </IconButton>
            <IconButton
              icon={<FaList size={12} />}
              onClick={handleRoleList}
              title="Role list"
              className="tw-hidden sm:tw-inline-flex"
            >
              <span className="tw-hidden xl:tw-inline">List</span>
            </IconButton>

            <ActionDropdown
              align="end"
              trigger={<FaEllipsisV size={12} />}
              items={[
                { label: 'Edit', icon: <MdEdit size={14} />, onClick: handleEditRole },
                { label: 'Combine roles', icon: <LuMerge size={14} />, onClick: () => setShowCombine(true) },
                { divider: true },
                { label: 'Delete', icon: <MdDelete size={14} />, danger: true, onClick: handleDeleteRole },
              ]}
            />
          </div>
        </div>
      </div>

      <RoleForm
        showModal={showRoleForm}
        setshowModal={setShowRoleForm}
        title={title}
        role={roleData}
      />

      <RoleDetailClone
        showModal={showCloneForm}
        setshowModal={setShowCloneForm}
        role={role}
      />

      {/* Ở đây role đang xem luôn là role được GIỮ — người dùng vào từ trang của
          nó, gộp role khác vào chứ không phải xoá chính nó. */}
      <CombineRolesDrawer
        open={showCombine}
        setOpen={setShowCombine}
        roles={combinableRoles}
        lockedTargetId={role?.id}
        onCombined={() => { dispatch(getRole(role.id)); setTrigger(v => v + 1); }}
      />
    </>
  );
};

RoleDetailHeader.propTypes = {
  role:       PropTypes.object,
  setTrigger: PropTypes.func.isRequired,
};

export default RoleDetailHeader;
